import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader';
import { SearchHeader } from '@ifca-root/react-component/src/components/Header/SearchHeader';
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import EmptyList from '@ifca-root/react-component/src/components/CardList/EmptyList';
import {
	AppBar,
	Divider,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemSecondaryAction,
	ListItemText,
	makeStyles,
	Menu,
	MenuItem,
	Radio,
	Tab,
	Tabs,
	TextField,
	Toolbar,
} from '@material-ui/core';
import {
	Tune,
	CloudDownload,
	Check,
	UnfoldMore,
	Sort,
	SwapVert,
} from '@material-ui/icons';
import AppContext from 'containers/App/Store/AppContext';
import {
	PosPermission,
	useGetAssignedOutletByUserQuery,
	useGetOutletQuery,
	useHasOpeningBalanceImportedQuery,
} from 'generated/graphql';
import React, { useContext, useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { startOfDay, endOfDay, format } from 'date-fns';
import { StockBalanceTab } from './Tabs/StockBalanceTab';
import { StockTransactionTab } from './Tabs/StockTransactionTab';
import { SoldOutTab } from './Tabs/SoldOutTab';
import FloatButton from '@ifca-root/react-component/src/components/Button/FloatButton';
import { StockMonitoringFilterDialog } from './FilterDialog';
import { ImportExportDialog } from './ImportExportDialog';
import { SortOption } from './SortDialog';
import PublishIcon from '@material-ui/icons/Publish';
import { useFuseSearch } from 'helpers/hooks/useSearch';
import { OutletIcon } from 'assets';
import { CommonDialog } from '@ifca-root/react-component/src/components/Dialog/CommonDialog';
import { usePermissionChecker } from 'helpers/hooks/usePermissionChecker';
function a11yProps(index: any) {
	return {
		id: `stock-tab-${index}`,
		'aria-controls': `stock-tabpanel-${index}`,
	};
}

interface TabPanelProps {
	children?: React.ReactNode;
	index: any;
	value: any;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`stock-tabpanel-${index}`}
			aria-labelledby={`stock-tab-${index}`}
			style={{ padding: '0px' }}
			{...other}
		>
			{value === index && <>{children}</>}
		</div>
	);
}

export const StockMonitoringList = () => {
	const { outletID }: any = useParams();
	const { globalState, dispatch }: any = useContext(AppContext as any);
	const history = useHistory();
	const location = useLocation();

	// Get tab from URL query parameter, default to 0 (Stock Balance)
	const getInitialTab = () => {
		const searchParams = new URLSearchParams(location.search);
		const tabParam = searchParams.get('tab');
		if (tabParam !== null) {
			const tab = parseInt(tabParam, 10);
			if (!isNaN(tab) && tab >= 0 && tab <= 2) {
				return tab;
			}
		}
		return 0;
	};

	const [openSwitch, setOpenSwitch] = useState(null);
	const [tabValue, setTabValue] = useState(getInitialTab());
	const { handlePermDisabled } = usePermissionChecker();

	const hideStockList = handlePermDisabled({
		outletID: outletID,
		permEnum: PosPermission.StockMonitoringShow,
	});

	const {
		data: { getAssignedOutletByUser } = { getAssignedOutletByUser: [] },
		loading: getAssignedOutletByUserLoading,
	} = useGetAssignedOutletByUserQuery({
		fetchPolicy: 'network-only',
	});

	const {
		filteredList: filteredList,
		handleSearch: handleSearch,
		setOriginalListing: setOriginalListing,
	} = useFuseSearch();

	useEffect(() => {
		if (getAssignedOutletByUser && getAssignedOutletByUser?.length > 0) {
			setOriginalListing(getAssignedOutletByUser);
		}
	}, [getAssignedOutletByUser]);
	// Update tab when URL query parameter changes
	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const tabParam = searchParams.get('tab');
		if (tabParam !== null) {
			const tab = parseInt(tabParam, 10);
			if (!isNaN(tab) && tab >= 0 && tab <= 2) {
				setTabValue(tab);
			}
		}
	}, [location.search]);

	// Default to today's date (start and end of day)
	const getTodayStartDate = () => {
		const today = new Date();
		return startOfDay(today);
	};

	const getTodayEndDate = () => {
		const today = new Date();
		return endOfDay(today);
	};

	const [startDate, setStartDate] = useState<Date | null>(getTodayStartDate());
	const [endDate, setEndDate] = useState<Date | null>(getTodayEndDate());
	const [searchQuery, setSearchQuery] = useState('');
	const [openFilterDialog, setOpenFilterDialog] = useState(false);
	const [openImportExportDialog, setOpenImportExportDialog] = useState(false);
	const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState<null | HTMLElement>(
		null,
	);
	const [sortOption, setSortOption] = useState<SortOption | null>(
		'transactionDateDesc',
	);

	const [transactionType, setTransactionType] = useState<string | null>(null);

	// Check if opening balance has already been imported
	const { data: importStatusData } = useHasOpeningBalanceImportedQuery({
		variables: { outletID },
		fetchPolicy: 'cache-and-network',
	});

	const hasImported = importStatusData?.hasOpeningBalanceImported || false;

	// Helper functions for filter dialog (returns YYYY-MM-DD strings)
	const getTodayDate = () => {
		const today = new Date();
		return format(startOfDay(today), 'yyyy-MM-dd');
	};

	const getTodayEndDateISO = () => {
		const today = new Date();
		return format(endOfDay(today), 'yyyy-MM-dd');
	};

	const [filterValue, setFilterValue] = useState<any>({
		startDate: getTodayDate(),
		endDate: getTodayEndDateISO(),
		itemName: '',
		category: '',
		transactionType: '',
	});

	const initialFilterValue = {
		startDate: getTodayDate(),
		endDate: getTodayEndDateISO(),
		itemName: '',
		category: '',
		transactionType: '',
	};

	// Debug log
	useEffect(() => {
		console.log('[StockMonitoring] Component mounted', { outletID });
	}, [outletID]);

	const {
		called: outletCalled,
		loading: outletLoading,
		data: { getOutlet } = { getOutlet: [] },
	} = useGetOutletQuery({
		fetchPolicy: 'network-only',
		variables: { ID: outletID },
	});

	const handleDrawer = () => {
		dispatch({
			type: 'drawerOpen',
			payload: !globalState.drawerOpen,
		});
	};

	const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
		setTabValue(newValue);
		// Remove tab query parameter from URL when user manually changes tabs
		history.replace(`/menu/outlet-app/${outletID}/stock-monitoring`);
	};

	const handleDateChange = (direction: 'prev' | 'next') => {
		const daysToAdd = direction === 'next' ? 1 : -1;
		const newStartDate = new Date(startDate || new Date());
		newStartDate.setDate(newStartDate.getDate() + daysToAdd);
		setStartDate(newStartDate);

		const newEndDate = new Date(endDate || new Date());
		newEndDate.setDate(newEndDate.getDate() + daysToAdd);
		setEndDate(newEndDate);
	};

	const triggerFilterDialogOpen = () => {
		setOpenFilterDialog(true);
		const savedFilter = JSON.parse(
			localStorage.getItem('stockMonitoringFilterArgs') || 'null',
		);
		if (savedFilter) {
			setFilterValue({
				startDate: savedFilter.startDate || getTodayDate(),
				endDate: savedFilter.endDate || getTodayEndDate(),
				itemName: savedFilter.itemName || '',
				category: savedFilter.category || '',
				transactionType: savedFilter.transactionType || '',
			});
		} else {
			// Set default to today if no saved filter
			setFilterValue({
				startDate: getTodayDate(),
				endDate: getTodayEndDate(),
				itemName: '',
				category: '',
				transactionType: '',
			});
		}
	};

	const triggerFilterDialogClose = () => {
		setOpenFilterDialog(false);
	};

	const handleFilterApply = (filter: any) => {
		// Apply filter logic here
		// This will be called when user confirms the filter
		console.log('Filter applied:', filter);
		// You can update the date range or trigger data refetch here
		if (filter.startDate) {
			setStartDate(new Date(filter.startDate));
		}
		if (filter.endDate) {
			setEndDate(new Date(filter.endDate));
		}
  	setTransactionType(filter.transactionType ? filter.transactionType : null);
	};

	// const stocktabsstyling = globalState.drawerOpen ? 'fixed-search-header-fw' : 'fixed-search-header';
	const stocktabsstyling = globalState.drawerOpen ? 'drawer-open' : '';

	return (
		<>
			{getAssignedOutletByUserLoading && <Loading />}
			{outletLoading && <Loading />}
			<MainHeader
				onClick={handleDrawer}
				mainBtn="menu"
				smTitle="Outlet App"
				sideBtn="switch"
				onClickAction={() => setOpenSwitch(true)}
				title={
					getAssignedOutletByUser?.filter(outlet => outlet?.ID === outletID)[0]
						?.name || 'Loading...'
				}
				routeSegments={[{ name: 'Stock Monitoring', current: true }]}
			/>

			<SearchHeader
				title="Stock Monitoring"
				value={searchQuery}
				onChangeAction={e => {
					setSearchQuery(e?.target?.value || '');
				}}
				fixed
				search
				onCloseAction={() => setSearchQuery('')}
				option={{
					icon: (
						<>
							<IconButton onClick={() => setOpenImportExportDialog(true)}>
								<PublishIcon />
							</IconButton>
							<IconButton onClick={triggerFilterDialogOpen}>
								<Tune />
							</IconButton>
							{tabValue === 1 && (
								<IconButton
									onClick={e => setSortMenuAnchorEl(e.currentTarget)}
									aria-controls={sortMenuAnchorEl ? 'sort-menu' : undefined}
									aria-haspopup="true"
								>
									<SwapVert />
								</IconButton>
							)}
						</>
					),
				}}
			/>

			<div className={`core-tab-bill-listing fixed-search-header ${stocktabsstyling}`}>
				<AppBar position="static">
					<Tabs
						aria-label="stock monitoring tabs"
						indicatorColor="primary"
						textColor="primary"
						value={tabValue}
						onChange={handleTabChange}
						variant="fullWidth"
					>
						<Tab
							label="Stock Balance"
							style={{ textTransform: 'none' }}
							{...a11yProps(0)}
						/>
						<Tab
							label="Stock Transaction"
							style={{ textTransform: 'none' }}
							{...a11yProps(1)}
						/>
						<Tab
							label="Sold Out"
							style={{ textTransform: 'none' }}
							{...a11yProps(2)}
						/>
					</Tabs>
				</AppBar>
			</div>

			{/* Tab Content */}
			<ContentWrapper dynamicInfoSearch float>
				<TabPanel value={tabValue} index={0}>
					{hideStockList ? (
						<EmptyList title="No record found" />
					) : (
						<StockBalanceTab
							outletID={outletID}
							startDate={startDate}
							endDate={endDate}
							searchQuery={searchQuery}
						/>
					)}
				</TabPanel>
				<TabPanel value={tabValue} index={1}>
					{hideStockList ? (
						<EmptyList title="No record found" />
					) : (
						<StockTransactionTab
							outletID={outletID}
							startDate={startDate}
							endDate={endDate}
							searchQuery={searchQuery}
							sortOption={sortOption}
							transactionType={transactionType}
						/>
					)}

					<FloatButton
						disabled={handlePermDisabled({
							outletID: outletID,
							permEnum: PosPermission.StockMonitoringShow,
						})}
						onClick={() => {
							if (hideStockList) return;
							history.push(
								`/menu/outlet-app/${outletID}/stock-monitoring/stock-transaction`,
							);
						}}
					/>
				</TabPanel>
				<TabPanel value={tabValue} index={2}>
					{hideStockList ? (
						<EmptyList title="No record found" />
					) : (
						<SoldOutTab
							outletID={outletID}
							startDate={startDate}
							endDate={endDate}
							searchQuery={searchQuery}
						/>
					)}
				</TabPanel>
			</ContentWrapper>
			<CommonDialog
				fullWidth={true}
				open={openSwitch}
				onClose={() => setOpenSwitch(false)}
				sections={{
					header: {
						dynamic: (
							<div className="">
								<div className="dialog-dynamic-content">
									<div className="session">
										<div className="title">{`Switch Outlet`}</div>
									</div>
								</div>
							</div>
						),
					},
					body: () => (
						<>
							{getAssignedOutletByUser?.length > 2 ? (
								<TextField
									// className="m-b-20"
									id="standard-basic"
									label="Search here..."
									fullWidth
									onChange={e => {
										handleSearch(`'"${e?.target?.value}"`, ['name']);
									}}
								/>
							) : null}
							<ContentWrapper style={{ marginTop: '0px' }}>
								<List>
									{filteredList?.map((outlet, index) => (
										<>
											<ListItem
												className={
													outlet?.ID === outletID ? 'bg-blue-light' : null
												}
											>
												<div style={{ paddingRight: '6px' }}>
													{outlet?.logo?.fileURL ? (
														<img
															src={outlet?.logo?.fileURL}
															style={{
																display: 'flex',
																marginTop: '2px',
																marginBottom: '2px',
																marginRight: '4px',
																width: '40px',
																height: '40px',
																justifyItems: 'center',
																borderRadius: '30px',
															}}
															loading="lazy"
														/>
													) : (
														<img
															src={OutletIcon}
															style={{
																display: 'flex',
																marginTop: '7px',
																marginBottom: '8px',
																marginRight: '4px',
																width: '40px',
																height: '25px',
																justifyItems: 'center',
																borderRadius: '30px',
															}}
															loading="lazy"
														/>
													)}
												</div>
												<ListItemText
													primary={
														<span className="xsTitle">{outlet?.name}</span>
													}
													onClick={() => {
														history.push(
															`/menu/outlet-app/${outlet?.ID}/stock-monitoring`,
														);
														setOpenSwitch(false);
														localStorage.setItem('latestOutlet', outlet?.ID);
													}}
												/>
												{outlet?.ID === outletID ? (
													<ListItemSecondaryAction>
														<IconButton edge="end" aria-label="delete">
															<Check color="primary" />
														</IconButton>
													</ListItemSecondaryAction>
												) : null}
											</ListItem>
											<Divider
												// variant="fullWidth"
												style={{ display: 'flex', width: '100%' }}
											/>
										</>
									))}
								</List>
							</ContentWrapper>
						</>
					),
					footer: {
						actions: [
							{
								displayText: 'Close',
								props: {
									onClick: () => setOpenSwitch(false),
									variant: 'contained',
									color: 'primary',
								},
							},
						],
					},
				}}
			/>
			<StockMonitoringFilterDialog
				setFilterValue={setFilterValue}
				filterValue={filterValue}
				initialValue={initialFilterValue}
				onApply={handleFilterApply}
				open={openFilterDialog}
				close={triggerFilterDialogClose}
				isStockTransactionTab={tabValue === 1}
			/>

			<ImportExportDialog
				open={openImportExportDialog}
				onClose={() => setOpenImportExportDialog(false)}
				outletID={outletID}
				currentTab={tabValue}
			/>

			<Menu
				id="sort-menu"
				anchorEl={sortMenuAnchorEl}
				keepMounted
				open={Boolean(sortMenuAnchorEl)}
				onClose={() => setSortMenuAnchorEl(null)}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			>
				<MenuItem
					onClick={() => {
						setSortOption('transactionDateAsc');
						setSortMenuAnchorEl(null);
					}}
				>
					<ListItemIcon>
						<Radio checked={sortOption === 'transactionDateAsc'} size="small" />
					</ListItemIcon>
					<ListItemText primary="Transaction Date (A-Z)" />
				</MenuItem>
				<MenuItem
					onClick={() => {
						setSortOption('transactionDateDesc');
						setSortMenuAnchorEl(null);
					}}
				>
					<ListItemIcon>
						<Radio
							checked={sortOption === 'transactionDateDesc'}
							size="small"
						/>
					</ListItemIcon>
					<ListItemText primary="Transaction Date (Z-A)" />
				</MenuItem>
				<MenuItem
					onClick={() => {
						setSortOption('docNoAsc');
						setSortMenuAnchorEl(null);
					}}
				>
					<ListItemIcon>
						<Radio checked={sortOption === 'docNoAsc'} size="small" />
					</ListItemIcon>
					<ListItemText primary="Doc No (A-Z)" />
				</MenuItem>
				<MenuItem
					onClick={() => {
						setSortOption('docNoDesc');
						setSortMenuAnchorEl(null);
					}}
				>
					<ListItemIcon>
						<Radio checked={sortOption === 'docNoDesc'} size="small" />
					</ListItemIcon>
					<ListItemText primary="Doc No (Z-A)" />
				</MenuItem>
				<MenuItem
					onClick={() => {
						setSortOption('refNoAsc');
						setSortMenuAnchorEl(null);
					}}
				>
					<ListItemIcon>
						<Radio checked={sortOption === 'refNoAsc'} size="small" />
					</ListItemIcon>
					<ListItemText primary="Ref No (A-Z)" />
				</MenuItem>
				<MenuItem
					onClick={() => {
						setSortOption('refNoDesc');
						setSortMenuAnchorEl(null);
					}}
				>
					<ListItemIcon>
						<Radio checked={sortOption === 'refNoDesc'} size="small" />
					</ListItemIcon>
					<ListItemText primary="Ref No (Z-A)" />
				</MenuItem>
				<MenuItem
					onClick={() => {
						setSortOption('transactionTypeAsc');
						setSortMenuAnchorEl(null);
					}}
				>
					<ListItemIcon>
						<Radio checked={sortOption === 'transactionTypeAsc'} size="small" />
					</ListItemIcon>
					<ListItemText primary="Transaction Type (A-Z)" />
				</MenuItem>
				<MenuItem
					onClick={() => {
						setSortOption('transactionTypeDesc');
						setSortMenuAnchorEl(null);
					}}
				>
					<ListItemIcon>
						<Radio
							checked={sortOption === 'transactionTypeDesc'}
							size="small"
						/>
					</ListItemIcon>
					<ListItemText primary="Transaction Type (Z-A)" />
				</MenuItem>
			</Menu>
		</>
	);
};
